import { AIModel } from '@/types';

export interface ModelSettingsData {
  providerOrder: string[];
  disabledProviders?: string[];
  providerLogos?: Record<string, string>;
}

export const DEFAULT_PROVIDER_ORDER = [
  'cohere',
  'openai',
  'gemini',
  'deepseek',
  'maplept',
  'ollama_remote',
  'ollama_local',
  'custom',
  'local',
  'transformers',
];

export function loadModelSettings(): ModelSettingsData {
  if (typeof window === 'undefined') {
    return { providerOrder: DEFAULT_PROVIDER_ORDER, disabledProviders: [], providerLogos: {} };
  }

  const saved = localStorage.getItem('modelSettings');
  if (!saved) {
    return { providerOrder: DEFAULT_PROVIDER_ORDER, disabledProviders: [], providerLogos: {} };
  }

  try {
    const parsed = JSON.parse(saved) as Partial<ModelSettingsData>;
    return {
      providerOrder: Array.isArray(parsed.providerOrder) ? parsed.providerOrder : DEFAULT_PROVIDER_ORDER,
      disabledProviders: Array.isArray(parsed.disabledProviders) ? parsed.disabledProviders : [],
      providerLogos: parsed.providerLogos && typeof parsed.providerLogos === 'object' ? parsed.providerLogos : {},
    };
  } catch {
    return { providerOrder: DEFAULT_PROVIDER_ORDER, disabledProviders: [], providerLogos: {} };
  }
}

export function saveModelSettings(settings: ModelSettingsData) {
  if (typeof window === 'undefined') return;
  localStorage.setItem('modelSettings', JSON.stringify(settings));
}

export function isProviderEnabled(providerId: string) {
  const settings = loadModelSettings();
  return !(settings.disabledProviders || []).includes(providerId);
}

export function getModelProviderKey(model: AIModel) {
  if (model.provider === 'ollama') {
    return `ollama_${model.source ?? 'remote'}`;
  }

  return model.provider;
}

export function isModelEnabled(model: AIModel) {
  return model.is_active !== false && isProviderEnabled(getModelProviderKey(model));
}

export function filterEnabledModels(models: AIModel[]) {
  const uniqueModels = new Map<string, AIModel>();

  models.forEach((model) => {
    if (isModelEnabled(model)) {
      uniqueModels.set(model.id, model);
    }
  });

  return Array.from(uniqueModels.values());
}

export function getProviderLogoOverride(providerId: string) {
  const settings = loadModelSettings();
  return settings.providerLogos?.[providerId] || '';
}

export function saveProviderLogoOverride(providerId: string, logoUrl: string) {
  const settings = loadModelSettings();
  const providerLogos = { ...(settings.providerLogos || {}) };

  if (logoUrl) {
    providerLogos[providerId] = logoUrl;
  } else {
    delete providerLogos[providerId];
  }

  saveModelSettings({
    ...settings,
    providerLogos,
  });
}